/**
 *
 * ContacDetails
 * Show contact details for user
 */

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import hasData from 'utils/hasData.js';
import { Card } from 'react-bootstrap';
import AvatarWithText from 'components/media/AvatarWithText/index.jsx';
import { formatPhoneNumber } from 'components/user/phoneNumber.js';
import EmergencyContactsCard from 'components/user/EmergencyContactsCard.jsx';
import { PARENT_ROLE } from 'containers/User/constants.js';

import { FormattedMessage } from 'react-intl';
import messages from '../messages.js';

const ContactBody = styled(Card.Body)`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  > * {
    width: 100%;
    min-width: 0;
    margin-right: 0 !important;
  }

  .avatar-icon {
    width: 20px;
    color: ${(props) => props.theme.brandPrimary}
  }

  .avatar-text,
  .avatar-text a {
    display: block;
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: break-word;
    white-space: normal;
  }
`;

function ContactDetails(props) {
  const { user } = props;
  const contactInfo = [];
  if (hasData(user.address)) {
    contactInfo.push({ icon: 'ss-home', text: addressString(user.address) });
  }
  const emails = contactDetailsEmails(user);
  if (emails.length > 0) {
    contactInfo.push({ icon: 'ss-mail', text: emails.map(emailLink) });
  }
  const phoneDisplay = contactDetailsPhoneDisplay(user);
  if (phoneDisplay) {
    contactInfo.push({ icon: 'ss-phone', text: phoneDisplay });
  }
  const showEmergency = user.roleType === PARENT_ROLE && hasData(user.emergencyContacts);

  return (
    <>
      <Card className="border-0">
        <Card.Header>
          <h2 className="card-title"><FormattedMessage {...messages.contactDetails} /></h2>
        </Card.Header>
        <ContactBody className="px-0">
          { contactInfo.map((item) => <AvatarWithText key={item.icon} bsSize="small" icon={item.icon}>{item.text}</AvatarWithText>)}
          { contactInfo.length === 0 && <FormattedMessage {...messages.noContactInfo} /> }
        </ContactBody>
      </Card>
      {showEmergency && (
        <EmergencyContactsCard
          emergencyContacts={user.emergencyContacts}
          className="border-0 mt-4"
        />
      )}
    </>
  );
}

ContactDetails.propTypes = {
  user: PropTypes.object,
};

export default ContactDetails;

function addressString(address) {
  return (
    <span>
      {address.street}
      {' '}
      {address.streetNumber}
      {address.streetNumberAdd || ''}
      <br />
      {address.postalCode}
      {' '}
      {address.city}
    </span>
  );
}

function emailLink(address) {
  return <a key={address} href={`mailto:${address}`}>{address}</a>;
}

/** Emails the API exposes to the current viewer (not parent-only `emailAddresses`). */
function contactDetailsEmails(user) {
  const emails = [];
  const add = (value) => {
    const email = typeof value === 'string' ? value.trim() : '';
    if (email && !emails.includes(email)) {
      emails.push(email);
    }
  };
  if (hasData(user.emailAddresses)) {
    user.emailAddresses.forEach((item) => add(typeof item === 'string' ? item : item?.value || item?.email));
  }
  if (typeof user.email === 'string') {
    add(user.email);
  } else if (user.email?.email) {
    add(user.email.email);
  }
  return emails;
}

/** Only `phoneNumbers` — pre-filtered by the API for the viewer (profile selection + sharing). */
function contactDetailsPhoneDisplay(user) {
  if (!hasData(user.phoneNumbers)) {
    return null;
  }
  const formatted = user.phoneNumbers
    .map((phone) => formatPhoneNumber(phone))
    .filter(Boolean);
  return formatted.length > 0 ? formatted.join(', ') : null;
}
